import { Signal } from '@angular/core';
import { AssetCategory, AssetCategoryIconStyle, PREDEFINED_ASSET_CATEGORY_ICON } from '@simOn/asset-categories/models';
import { TAsset } from '@simOn/asset/modifying/models';
import { LastEditedType } from '@simOn/common/models';
import { RoomFilterOption, toMergedRoomOptions, toSelectedRoomIds } from '@simOn/room/models';
import { AssetListFilters, AssetListFiltersOptions, toAssetCategoryItem } from './assets.model';

const HOUR_IN_MS = 60 * 60 * 1000;

export interface FilterSelection {
  spaces: string[];
  /** Selected room option values (`AssetRoomFilterOption.value`), not raw room ids. */
  rooms: string[];
  categories: string[];
  creators: string[];
  lastEdit: number | undefined;
}

export interface SpaceOption {
  value: string;
  label: string;
}

export const EMPTY_FILTERING_OPTIONS: AssetListFiltersOptions = {
  categories: [],
  editedAtFrom: '',
  editedAtTo: '',
  authors: [],
  rooms: [],
  apartments: []
};

/**
 * Contract between the asset filters dialog and the state that backs it.
 * Implemented by the assets page store and by the 3D-walk filters state.
 * The state owns the filtering options: it loads them for the applied
 * selection and reloads them after `applyFilters` — the dialog never
 * fetches them itself while the user edits a draft.
 */
export interface AssetFiltersStore {
  readonly spaceScoped: Signal<boolean>;
  /** Filtering options loaded for the currently applied selection. */
  readonly filteringOptions: Signal<AssetListFiltersOptions>;
  readonly selectedSpaces: Signal<string[]>;
  /** Selected room option values (`AssetRoomFilterOption.value`), not raw room ids. */
  readonly selectedRooms: Signal<string[]>;
  readonly selectedCategories: Signal<string[]>;
  readonly selectedCreators: Signal<string[]>;
  readonly selectedLastEdit: Signal<number | undefined>;
  applyFilters(selection: FilterSelection): void;
}

export function toSpaceOptions(options: AssetListFiltersOptions): SpaceOption[] {
  return options.apartments.map((apartment) => ({ value: apartment.apartmentId, label: apartment.apartmentName }));
}

/** A room filter option representing every room sharing one case-insensitive name across apartments. */
export type AssetRoomFilterOption = RoomFilterOption;

export function toRoomOptions(options: AssetListFiltersOptions): AssetRoomFilterOption[] {
  return toMergedRoomOptions(options.rooms);
}

/** A category filter option representing every category sharing one case-insensitive name. */
export interface AssetCategoryFilterOption {
  /** Option value stored in the filter selection — the normalized category name. */
  value: string;
  /** Ids of all categories merged under this option; the API receives them as `categoryIds`. */
  categoryIds: string[];
  name: string;
  /** Category rendered in the option label; synthetic (`MERGED_CATEGORY_ICON`) when the merged categories differ in icons. */
  category: AssetCategory;
}

/**
 * Icon rendered for a merged category option whose source categories have
 * different icons (glyph, colors or image). The dedicated "merged category"
 * icon is not designed yet — substitute it here (`icon`/`iconStyle`/colors)
 * once it lands.
 */
export const MERGED_CATEGORY_ICON: Pick<
  AssetCategory,
  'iconStyle' | 'icon' | 'iconColor' | 'iconColorBackground' | 'imageId' | 'image' | 'imageUrlWithSasToken' | 'scale'
> = {
  iconStyle: AssetCategoryIconStyle.BlackWhite,
  icon: PREDEFINED_ASSET_CATEGORY_ICON.Other,
  iconColor: null,
  iconColorBackground: null,
  imageId: null,
  image: null,
  imageUrlWithSasToken: null,
  scale: null
};

const MIXED_ICON_SIGNATURE = '__mixed__';

/** Everything that affects how `sim-asset-category-icon` renders the category. */
function categoryIconSignature(category: AssetCategory): string {
  const customColor = category.iconStyle === AssetCategoryIconStyle.CustomColor;
  return [
    category.iconStyle,
    category.icon ?? PREDEFINED_ASSET_CATEGORY_ICON.Other,
    customColor ? category.iconColor : null,
    customColor ? category.iconColorBackground : null,
    category.imageId ?? null,
    category.scale ?? 1
  ].join('|');
}

/**
 * Merges categories whose display names differ only in letter case into a
 * single option. The merged option keeps the shared icon when all source
 * categories render the same one, otherwise it falls back to `MERGED_CATEGORY_ICON`.
 */
export function toCategoryOptions(options: AssetListFiltersOptions): AssetCategoryFilterOption[] {
  const groups = new Map<string, { option: AssetCategoryFilterOption; iconSignature: string }>();
  for (const category of options.categories) {
    const name = toAssetCategoryItem(category).name;
    const value = name.trim().toLowerCase();
    const iconSignature = categoryIconSignature(category);
    const group = groups.get(value);
    if (group) {
      group.option.categoryIds.push(category.id);
      if (group.iconSignature !== MIXED_ICON_SIGNATURE && group.iconSignature !== iconSignature) {
        group.iconSignature = MIXED_ICON_SIGNATURE;
        group.option.category = { ...group.option.category, ...MERGED_CATEGORY_ICON };
      }
    } else {
      groups.set(value, {
        option: { value, categoryIds: [category.id], name, category },
        iconSignature
      });
    }
  }
  return [...groups.values()].map((group) => group.option);
}

/** Expands selected category option values to the category ids they represent. */
export function toSelectedCategoryIds(
  selectedCategories: string[],
  categoryOptions: AssetCategoryFilterOption[]
): string[] {
  const selected = new Set(selectedCategories);
  return categoryOptions.filter((option) => selected.has(option.value)).flatMap((option) => option.categoryIds);
}

/**
 * Maps a filter selection to the API list-filter payload. `selection.rooms`
 * and `selection.categories` hold merged option values — pass `roomOptions` /
 * `categoryOptions` to expand them to the ids the API expects.
 */
export function toAssetListFilters(
  selection: FilterSelection,
  extras: {
    apartmentIds?: string[];
    name?: string | undefined;
    roomOptions?: AssetRoomFilterOption[];
    categoryOptions?: AssetCategoryFilterOption[];
  } = {}
): Partial<AssetListFilters> {
  const filters: Partial<AssetListFilters> = {
    categoryIds: extras.categoryOptions
      ? toSelectedCategoryIds(selection.categories, extras.categoryOptions)
      : selection.categories,
    roomIds: extras.roomOptions ? toSelectedRoomIds(selection.rooms, extras.roomOptions) : selection.rooms,
    authorIds: selection.creators,
    name: extras.name || undefined
  };
  if (selection.lastEdit !== undefined && selection.lastEdit > 0) {
    const now = new Date();
    filters.lastEdited = selection.lastEdit as LastEditedType;
    filters.editedAtTo = now.toISOString();
    filters.editedAtFrom = new Date(now.getTime() - selection.lastEdit * HOUR_IN_MS).toISOString();
  }
  if (extras.apartmentIds && extras.apartmentIds.length > 0) filters.apartmentIds = [...extras.apartmentIds];
  return filters;
}

/**
 * Client-side equivalent of the API list filters, for places where assets are
 * already loaded in memory (the 3D walk). The `spaces` selection is ignored —
 * the walk always shows a single space.
 */
export function toAssetFilterPredicate(
  selection: FilterSelection,
  roomOptions: AssetRoomFilterOption[],
  categoryOptions: AssetCategoryFilterOption[]
): (asset: TAsset) => boolean {
  const rooms = new Set(toSelectedRoomIds(selection.rooms, roomOptions));
  const categories = new Set(toSelectedCategoryIds(selection.categories, categoryOptions));
  const creators = new Set(selection.creators);
  const editedFrom =
    selection.lastEdit !== undefined && selection.lastEdit > 0
      ? Date.now() - selection.lastEdit * HOUR_IN_MS
      : undefined;

  return (asset: TAsset): boolean => {
    if (rooms.size > 0 && !rooms.has(asset.roomId)) return false;
    if (categories.size > 0 && !categories.has(asset.categoryId ?? asset.category?.id ?? '')) return false;
    if (creators.size > 0 && !creators.has(asset.author?.userId ?? '')) return false;
    if (editedFrom !== undefined) {
      const edited = Date.parse(asset.editedAt ?? asset.createdAt);
      if (Number.isNaN(edited) || edited < editedFrom) return false;
    }
    return true;
  };
}
